import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Paper, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const ObsCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f0f8ff',
  padding: '12px',
  borderRadius: '6px',
  border: '1px solid #b3d9ff',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#00798E',
  marginBottom: '10px',
  paddingBottom: '6px',
  borderBottom: '1px solid #d0e8f0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&::before': {
    content: '""',
    width: '3px',
    height: '13px',
    backgroundColor: '#4caf50',
    borderRadius: '2px',
  },
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginBottom: '8px',
  flexWrap: 'wrap',
}));

const InfoItem = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: '150px',
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: '600',
  color: '#7b809a',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '3px',
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: '600',
  color: '#344767',
}));

const DataTable = styled(Table)(({ theme }) => ({
  marginTop: '8px',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid #e0e0e0',
  },
  '& .table-header-row .MuiTableCell-root': {
    backgroundColor: '#f8f9fa',
    fontWeight: '700',
    color: '#128296',
    fontSize: '10px',
    textAlign: 'center',
  },
  '& .table-header-row .MuiTableCell-root:first-of-type': {
    textAlign: 'left',
  },
  '& .table-data-row': {
    '& .MuiTableCell-root': {
      fontSize: '10px',
      padding: '8px 12px',
    },
    '&:hover': {
      backgroundColor: '#fafbfc',
    },
  },
  '& .MuiTableCell-root:last-child': {
    borderRight: 'none',
  },
}));

function ObstetricHistoryDisplay({ obstetricHistory, miscarriageHistories }) {
  const theme = useTheme();

  if (!obstetricHistory && (!miscarriageHistories || miscarriageHistories.length === 0)) {
    return null;
  }

  const formatInfertilityType = (type) => {
    if (!type) return 'N/A';
    const typeMap = {
      'primary': 'Primary Infertility',
      'secondary': 'Secondary Infertility',
      'boh': 'BOH',
      'gynecological': 'Gynecological Problems',
    };
    return typeMap[type.toLowerCase()] || type;
  };

  return (
    <ObsCard>
      {obstetricHistory && (
        <>
          <SectionTitle>
            {formatInfertilityType(obstetricHistory.infetrtility_type)} [{obstetricHistory.para_p1 || '0'}+{obstetricHistory.para_p2 || '0'}]
          </SectionTitle>

          <InfoRow>
            <InfoItem>
              <InfoLabel>No. of Miscarriages</InfoLabel>
              <InfoValue>{obstetricHistory.no_of_miscarriages || '0'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Live Issues</InfoLabel>
              <InfoValue>{obstetricHistory.live_issues || 'N/A'}</InfoValue>
            </InfoItem>
          </InfoRow>

          {obstetricHistory.remarks && (
            <Box sx={{ mt: 1, mb: 1.5 }}>
              <InfoLabel>Remarks</InfoLabel>
              <Box sx={{ fontSize: '11px', color: '#344767', fontStyle: 'italic', mt: 0.5 }}>
                <span dangerouslySetInnerHTML={{ __html: obstetricHistory.remarks }} />
              </Box>
            </Box>
          )}

          {miscarriageHistories && miscarriageHistories.length > 0 && <Box sx={{ my: 1, borderTop: '1px solid #d0e8f0' }} />}
        </>
      )}

      {miscarriageHistories && miscarriageHistories.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <DataTable size="small">
              <TableBody>
                <TableRow className="table-header-row">
                  <TableCell sx={{ textAlign: 'left' }}>Para No.</TableCell>
                  <TableCell>Weeks</TableCell>
                  <TableCell sx={{ textAlign: 'left' }}>Type</TableCell>
                  <TableCell>D&amp;E</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell sx={{ textAlign: 'left' }}>Remarks</TableCell>
                </TableRow>
                {miscarriageHistories.map((item, idx) => (
                  <TableRow key={idx} className="table-data-row">
                    <TableCell sx={{ fontWeight: '600', color: '#128296', textAlign: 'left' }}>
                      {item.para_number || '-'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {item.weeks_of_miscarriage || '-'}
                    </TableCell>
                    <TableCell sx={{ color: '#00798E', fontWeight: '600', textAlign: 'left' }}>
                      {item.value || item.param_group_member_option?.value || '-'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {item.de_done === true || item.de_done === 'true' ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {item.year_of_miscarriage || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '9px' }}>
                      {item.remarks ? (
                        item.remarks.includes('<') && item.remarks.includes('>') ? (
                          <span dangerouslySetInnerHTML={{ __html: item.remarks }} />
                        ) : (
                          item.remarks
                        )
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </TableContainer>
        </Box>
      )}
    </ObsCard>
  );
}

ObstetricHistoryDisplay.propTypes = {
  obstetricHistory: PropTypes.shape({
    infetrtility_type: PropTypes.string,
    para_p1: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    para_p2: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    no_of_miscarriages: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    live_issues: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    remarks: PropTypes.string,
  }),
  miscarriageHistories: PropTypes.arrayOf(
    PropTypes.shape({
      para_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      year_of_miscarriage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.string,
      param_group_member_option: PropTypes.shape({
        value: PropTypes.string,
      }),
      weeks_of_miscarriage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      de_done: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      remarks: PropTypes.string,
    })
  ),
};

export default ObstetricHistoryDisplay;
